import { Inventory, InventoryService } from '@services';
import { useStore } from '@stores';
import { Form } from 'antd';
import { observer } from 'mobx-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import Presentation from './InventoryItemDrawer.presentation';

export interface InventoryItemDrawerHandle {
  edit: (record: Inventory) => void;
}

interface IProps {
  categoryOptions: { label: string; value: string }[];
  onSuccess: () => void;
  onDelete?: (id: string) => Promise<void> | void;
}

export const InventoryItemDrawer = observer(
  forwardRef<InventoryItemDrawerHandle, IProps>(({ categoryOptions, onSuccess, onDelete }, ref) => {
    const { inventoryStore } = useStore();
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingDocuments, setEditingDocuments] = useState<Inventory['documents']>([]);
    const [itemInvoiceFile, setItemInvoiceFile] = useState<{
      base64: string;
      name: string;
      contentType: string;
    } | null>(null);
    const [previewDoc, setPreviewDoc] = useState<{
      name: string;
      src: string;
      contentType: string;
    } | null>(null);

    useImperativeHandle(ref, () => ({
      edit: (record: Inventory) => {
        setEditingId(record.id);
        setEditingDocuments((record.documents || []).filter(Boolean));
        form.setFieldsValue(record);
        setOpen(true);
      },
    }));

    const reset = () => {
      setOpen(false);
      form.resetFields();
      setEditingId(null);
      setEditingDocuments([]);
      setItemInvoiceFile(null);
      setPreviewDoc(null);
    };

    const handleSubmit = async () => {
      const values = await form.validateFields();
      let documentId: string | undefined;
      if (itemInvoiceFile) {
        const doc = await InventoryService.uploadInvoice({
          name: itemInvoiceFile.name,
          content: itemInvoiceFile.base64,
          contentType: itemInvoiceFile.contentType,
        });
        documentId = doc.id;
      }
      if (editingId) {
        await inventoryStore.update({ ...values, id: editingId, documentId });
      } else {
        await inventoryStore.create({ ...values, documentId });
      }
      reset();
      onSuccess();
    };

    const handleDelete = async () => {
      if (!editingId || !onDelete) return;
      try {
        await onDelete(editingId);
        reset();
      } catch (err) {
        // Backend already surfaces a toast via httpService; keep the drawer open so user can retry or close manually.
        console.error('Failed to delete inventory item', err);
      }
    };

    const handleItemInvoiceSelect = (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const fileObj = {
          base64: reader.result as string,
          name: file.name,
          contentType: file.type,
        };
        setItemInvoiceFile(fileObj);
        setPreviewDoc({
          name: fileObj.name,
          src: fileObj.base64,
          contentType: fileObj.contentType,
        });
      };
      reader.readAsDataURL(file);
    };

    return (
      <Presentation
        open={open}
        editingId={editingId}
        editingDocuments={editingDocuments}
        form={form}
        categoryOptions={categoryOptions}
        itemInvoiceFile={itemInvoiceFile}
        onClose={reset}
        onSubmit={handleSubmit}
        onItemInvoiceSelect={handleItemInvoiceSelect}
        onItemInvoiceRemove={() => {
          setItemInvoiceFile(null);
          setPreviewDoc(null);
        }}
        previewDoc={previewDoc}
        onPreviewDocument={setPreviewDoc}
        onClosePreview={() => setPreviewDoc(null)}
        onDelete={onDelete ? handleDelete : undefined}
      />
    );
  })
);
